import React, { useEffect, useMemo, useState } from "react";
import httpClient from "../../components/helpers/httpClient";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";

function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("nl-BE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getSubject(submission) {
  const sourceTag = submission?.source_tag || "";
  const source = submission?.source || "";
  const formType = submission?.form_type || "contact";
  const displaySource = sourceTag || source || "Website";

  if (formType === "claim-room") return `Room request${sourceTag ? ` - ${sourceTag}` : ""}`;
  if (formType === "contact") return `Contact via ${displaySource}`;
  return `Submission via ${displaySource}`;
}

export default function MobileNewContactsList({ onOpenSubmission }) {
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [searchWord, setSearchWord] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const limit = 25;

  const queryParams = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      ascending: "false",
    });
    if (searchWord) params.set("search", searchWord);
    params.set("sort", "submitted_at");
    return params.toString();
  }, [page, searchWord]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/new-contacts?${queryParams}`
      );
      setSubmissions(response?.data?.submissions || []);
      setTotalPages(response?.data?.total_pages || 1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const handleClear = () => {
    if (searchWord === "") return;
    setSearchWord("");
    setPage(1);
  };

  return (
    <div className="mobile-list">
      <div className="mobile-search" onClick={handleClear}>
        <SearchIcon style={{ color: "var(--secondairy-medium-gray)" }} />
        <input
          placeholder="Search new contact..."
          value={searchWord}
          onChange={(e) => {
            setSearchWord(e.target.value);
            setPage(1);
          }}
        />
        {searchWord === "" ? null : <ClearIcon />}
      </div>

      {loading && submissions.length === 0 ? (
        <div style={{ padding: "8px 4px", color: "var(--secondairy-medium-gray)" }}>
          Loading...
        </div>
      ) : submissions.length === 0 ? (
        <div style={{ padding: "8px 4px", color: "var(--secondairy-medium-gray)" }}>
          No submissions found.
        </div>
      ) : (
        submissions.map((s) => {
          const title =
            s?.contact_email ||
            `${s?.contact_first_name || ""} ${s?.contact_last_name || ""}`.trim() ||
            `Submission #${s?.id}`;
          const subtitle = [getSubject(s), s?.source_tag || s?.source, formatDate(s?.submitted_at)]
            .filter(Boolean)
            .join(" • ");
          return (
            <div
              key={s.id}
              className="mobile-row"
              onClick={() => onOpenSubmission?.(s)}
              style={{ cursor: "pointer" }}
            >
              <div style={{ overflow: "hidden" }}>
                <div className="mobile-row__title" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
                <div className="mobile-row__subtitle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{subtitle}</div>
              </div>
              <div className="mobile-row__right">
                <ChevronRightIcon style={{ color: "var(--secondairy-medium-gray)" }} />
              </div>
            </div>
          );
        })
      )}

      <div
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "space-between",
          alignItems: "center",
          padding: "6px 2px",
          color: "var(--secondairy-medium-gray)",
        }}
      >
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          style={{
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid var(--mobile-border)",
            background: "var(--mobile-card)",
            opacity: page <= 1 ? 0.5 : 1,
          }}
        >
          Prev
        </button>
        <div>
          Page {page} / {totalPages}
        </div>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          style={{
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid var(--mobile-border)",
            background: "var(--mobile-card)",
            opacity: page >= totalPages ? 0.5 : 1,
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
}


